// One shared player for audio cards (music, SFX, voice samples), so tapping a second sample stops the first.
// 2026-11-04 (Keaton's report: the music bed started and nothing could pause it): what plays is mirrored into
// `nowPlaying.audio` for the global Now playing chip (components/NowPlaying.tsx); a pause keeps the place, and tapping
// the same card again picks up from there.
import { set, toast, type NowPlaying } from "./store";

let el: HTMLAudioElement | null = null;
let current: string | null = null;
let currentTitle = "Sound";
let currentUrl = "";

const mirror = (np: NowPlaying | null) => set((s) => ({ nowPlaying: { ...s.nowPlaying, audio: np } }));

function cleared(key: string) {
  if (current !== key) return;
  current = null;
  set({ playing: null });
  mirror(null);
}

/** Play `url` as card `key`, or pause it when it's the one playing (`title` names it on the Now playing chip). */
export function toggleAudio(key: string, url: string | undefined | null, title?: string) {
  if (!url) {
    // "toggle off" callers pass no url (DirectorPicker, Settings): that's a pause, not a missing sound
    if (current === key && el && !el.paused) return pauseAudio();
    toast("That sound isn't ready to play yet.", "bad");
    return;
  }
  if (current === key && el) {
    if (!el.paused) return pauseAudio();
    return resumeAudio();
  }
  el?.pause();
  el = new Audio(url);
  current = key;
  currentTitle = title ?? "Sound";
  currentUrl = url;
  set({ playing: key });
  mirror({ url, title: currentTitle, paused: false, key });
  el.onended = el.onerror = () => cleared(key);
  el.play().catch(() => cleared(key));
}

/** Pause what the card player is playing (it stays loaded: resumeAudio or the same card's tap goes on from there). */
export function pauseAudio() {
  if (!el || !current || el.paused) return;
  el.pause();
  const key = current;
  set({ playing: null });
  mirror({ url: currentUrl, title: currentTitle, paused: true, key });
}

export function resumeAudio() {
  if (!el || !current || !el.paused) return;
  const key = current;
  set({ playing: key });
  mirror({ url: currentUrl, title: currentTitle, paused: false, key });
  el.play().catch(() => cleared(key));
}

/** Stop the card player. With `key`, only when that card is the one loaded (a tray row going away stops its own sound). */
export function stopAudio(key?: string) {
  if (key !== undefined && current !== key) return;
  el?.pause();
  current = null;
  set({ playing: null });
  mirror(null);
}
